import { useRef, useState } from "react";
import { X, FileText, Heart, Download } from "lucide-react";
import { API, api, apiErr } from "@/api";
import Overlay from "@/components/Overlay";
import ProductImages from "@/components/ProductImages";
import InterestTray from "@/components/InterestTray";
import { useInterests } from "@/hooks/useInterests";
import { toast } from "sonner";

export function ProductCard({ p, i, prefix = "modal", interest }) {
  const liked = interest?.liked.has(p.product_id);
  const [burst, setBurst] = useState(false);
  const last = useRef(0);
  const canLike = interest && p.product_id;

  const like = async (force) => {
    if (!canLike || (force && liked)) { return; }
    const err = await interest.toggle(p.product_id);
    if (err) { toast.error(err); }
  };
  const onTap = () => {
    const now = Date.now();
    if (now - last.current < 320) { setBurst(true); setTimeout(() => setBurst(false), 600); like(true); }
    last.current = now;
  };

  return (
    <div className="border border-amber-500/15 rounded-2xl overflow-hidden bg-white/40" data-testid={`${prefix}-product-${i}`}>
      <div className="relative select-none" onClick={canLike ? onTap : undefined}>
        <ProductImages product={p} testid={`${prefix}-product-image-${i}`} rounded="rounded-none" />
        {burst && <Heart size={72} className="absolute inset-0 m-auto text-white drop-shadow-lg animate-ping pointer-events-none" fill="currentColor" />}
        {canLike && (
          <button onClick={(e) => { e.stopPropagation(); like(false); }} className="absolute top-2 right-2 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center" data-testid={`${prefix}-like-${i}`} aria-pressed={liked}>
            <Heart size={17} className={liked ? "text-rose-500" : "text-slate-500"} fill={liked ? "currentColor" : "none"} />
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="text-slate-100 font-medium leading-tight">{p.name}</div>
        {p.category && <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">{p.category}</span>}
        {p.highlight && <p className="text-[11px] text-amber-300/90 mt-1">★ {p.highlight}</p>}
        {p.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{p.description}</p>}
        {(Array.isArray(p.materials) ? p.materials.length > 0 : !!p.materials) && (
          <p className="text-[10px] text-slate-500 mt-1">{Array.isArray(p.materials) ? p.materials.join(" · ") : p.materials}</p>
        )}
      </div>
    </div>
  );
}

export async function downloadPdf(url, title) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `${title || "catalogue"}.pdf`;
    a.click();
  } catch (err) { toast.error(apiErr(err)); }
}

export function ProductGrid({ products, prefix, interest }) {
  return (
    <div className="grid grid-cols-2 gap-3 mt-4">
      {products.map((p, i) => <ProductCard key={p.product_id || i} p={p} i={i} prefix={prefix} interest={interest} />)}
    </div>
  );
}

export default function CatalogueModal({ catalogue, onClose, visitor = false }) {
  const c = catalogue;
  const products = c.ai_content?.products || [];
  const interest = useInterests(c.id, visitor && c.type === "ai" && c.id !== "preview");
  const cover = c.ai_content?.cover_image;
  return (
    <Overlay onClose={onClose} className="max-w-2xl" testid="catalogue-viewer-modal">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-2xl gold-text leading-tight">{c.ai_content?.title || c.title}</h3>
          {c.exhibitor_name && <p className="text-[11px] text-slate-500">{c.exhibitor_name}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {c.type === "ai" && c.share_token && c.status !== "draft" && (
            <button onClick={() => downloadPdf(`/share/by-token/${c.share_token}/pdf`, c.title)} className="text-slate-400 hover:text-amber-300" title="Download PDF" data-testid="catalogue-modal-pdf"><Download size={18} /></button>
          )}
          <button onClick={onClose} className="text-slate-400 hover:text-amber-300" data-testid="catalogue-modal-close"><X size={20} /></button>
        </div>
      </div>

      {c.type === "ai" ? (
        <>
          {cover && <img src={`${API}/files/${cover}`} alt="" className="w-full aspect-[16/9] object-cover rounded-2xl mt-4" data-testid="catalogue-cover" />}
          {c.ai_content?.intro && <p className="text-slate-400 text-sm mt-3">{c.ai_content.intro}</p>}
          {interest && <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><Heart size={11} /> Tap the heart or double-tap a photo to select pieces you like.</p>}
          <ProductGrid products={products} prefix="modal" interest={interest} />
          {products.length === 0 && <p className="text-slate-500 text-sm mt-4">No products in this catalogue yet.</p>}
          {interest && <InterestTray interest={interest} exhibitorName={c.exhibitor_name} />}
        </>
      ) : (
        <div className="mt-5 text-center">
          <FileText size={28} className="text-amber-400 mx-auto" />
          <p className="text-slate-400 text-sm mt-2">PDF catalogue</p>
          {c.storage_path && (
            <a href={`${API}/files/${c.storage_path}`} target="_blank" rel="noreferrer" className="inline-block btn-gold px-6 py-3 rounded-xl mt-4" data-testid="catalogue-pdf-open">Open PDF</a>
          )}
        </div>
      )}
    </Overlay>
  );
}
